import { Injectable, NgZone } from "@angular/core";
import { HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { LoginService } from "./login.service";
import { environment } from "src/environments/environment";
import { ToastrService } from "ngx-toastr";
import { Constants } from "src/app/models/constants";

export const IS_PRIVATE_API = new HttpContextToken<boolean>(() => true);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    // Sets of Backend's URLs that should be excluded from certain interceptor operations
    private readonly excludedAuthUrls: Set<string> = new Set([
        `${environment.apiBaseUrl}/auth/sendotp`,
        `${environment.apiBaseUrl}/auth/login`,
        `${environment.apiBaseUrl}/jira/verifyJiraUserAndSendOtp`,
        `${environment.apiBaseUrl}/jira/verifyOtpAndActivateUser`,
        `${environment.apiBaseUrl}/auth/generateotp`,
        `${environment.apiBaseUrl}/auth/signup`,
        `${environment.apiBaseUrl}/register/getAllOptionsForRegistration`,
        `${environment.apiBaseUrl}/userInvite/getInviteDetails/`,
        `${environment.apiBaseUrl}/organization/exists`,
        `${environment.apiBaseUrl}/userInvite/getInviteDetails/`,
    ]);

    // AI server base URLs for secretKey injection when running on localhost
    private readonly aiServerBaseUrls: string[] = [
        environment.aiServerUrl,
        environment.aiServerUrl2,
        environment.aiServerUrlForTranscription,
        environment.aiServerUrlForMeetingTranscription,
        environment.aiHelpBotUrl,
    ].filter((url): url is string => !!url); // Filter out undefined/null values

    protected readonly Constants = Constants;

    constructor(
        private loginService: LoginService,
        private toastr: ToastrService,
        private ngZone: NgZone,
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Presigned S3 URLs (PT-16085) must receive NO custom headers — Authorization, timeZone or
        // screenName on an S3 request forces a CORS preflight the bucket rejects.
        // AttachmentDownloadService uses fetch() and never reaches the interceptor chain; this
        // guard protects accidental HttpClient calls to S3.
        if (request.url.includes(".amazonaws.com")) {
            return next.handle(request);
        }
        // Initialize headers with common values
        let headers = this.initializeHeaders(request);
        // Add authorization headers if the URL is not in the excludedAuthUrls set
        if (!this.excludedAuthUrls.has(request.url) && request.context.get(IS_PRIVATE_API)) {
            headers = this.addAuthorizationHeaders(request, headers);
        }
        // Add secretKey header for AI requests when running on localhost
        if (this.isLocalhost() && this.isAiServerRequest(request.url)) {
            headers = headers.set("secretKey", environment.aiServerSecretKeyForLocal);
        }
        // Clone the request with the new headers
        const authReq = request.clone({ headers });
        // Handle the request and catch any errors
        return next.handle(authReq).pipe(
            catchError((error: HttpErrorResponse) => {
                // If the error status is 499 and the user is logged in, log out the user
                if (error.status === 499 && this.loginService.isLoggedIn()) {
                    this.toastr.warning(Constants.ErrorMessages.ACCESS_CHANGED_MESSAGE);

                    // Delay logout by 2.5 seconds to show the message
                    setTimeout(() => {
                        this.ngZone.run(() => {
                            this.loginService.logoutTheUserAndRedirectToLogin();
                        });
                    }, 2500);
                }
                // Throw the error to be caught and handled by the component that initiated the request
                return throwError(() => error);
            }),
        );
    }

    private initializeHeaders(request: HttpRequest<any>): HttpHeaders {
        let headers = new HttpHeaders();
        request.headers.keys()?.forEach((header) => {
            headers = headers.set(header, request.headers.get(header)!);
        });
        headers = headers.set("timeZone", Intl.DateTimeFormat().resolvedOptions().timeZone);
        // check is screenName present in request
        try {
            if (request.headers?.get("screenName")) {
                headers = headers.set("screenName", request.headers.get("screenName")!);
            } else {
                headers = headers.set("screenName", `Dashboard`);
            }
        } catch (error) {
            console.log("Error in setting screenName in headers", error);
        }
        return headers;
    }

    private addAuthorizationHeaders(request: HttpRequest<any>, headers: HttpHeaders): HttpHeaders {
        let token: any = this.loginService.getToken();

        // check for token existence
        if (!token) {
            console.warn('No token available for authorization');
            return headers;
        }

        // Add Authorization and accountIds headers for all authenticated requests
        let decodedToken = this.loginService.getDecodedAccessToken(token);
        let accountIdsForHeader = this.loginService.getTokenAccountIds(decodedToken);

        headers = headers.set("Authorization", `Bearer ${token}`);
        if (request.headers.get("accountIds")) {
            headers = headers.set("accountIds", request.headers.get("accountIds")!);
        } else {
            headers = headers.set("accountIds", accountIdsForHeader);
        }

        return headers;
    }

    /**
     * Check if the application is running on localhost
     */
    private isLocalhost(): boolean {
        const hostname = window.location.hostname;
        return hostname === 'localhost' || hostname === '127.0.0.1';
    }

    /**
     * Check if the request URL is an AI server URL
     */
    private isAiServerRequest(url: string): boolean {
        return this.aiServerBaseUrls.some(baseUrl => url.startsWith(baseUrl));
    }
}
